import { client, log } from ".";
import { monedaLocalDePais } from "./brutoCapex";
import { getRateForOpp } from "./calculateVentas";
import { getDesglosePorEdificio, resolverCircuitosParticipantesMes } from "./comisionEdificiosUtil";
import { getDolarValue, loadExchangeRates } from "./exchangeRateService";

/**
 * Convierte el monto a congelar a moneda LOCAL con el TC de la fecha de la factura (10.185) — fuente
 * ÚNICA de esta conversión, usada por los 3 caminos que escriben `comision_snapshot.monto_congelado`
 * (`capturarSnapshotDesdeVenta`, `capturarSnapshotDesdeContrato`, el refresh por-mes de
 * `congelarPctIntermediacion.js`). Antes de este fix, `monto` se guardaba CRUDO (USD sin convertir
 * si `moneda_dolar=true`) y el motor (`calculateComission.js`) lo inyectaba sobre un `importe` que el
 * caller YA había convertido a local — deflactando la comisión ~900-1.000× (Chile) / ~3,3× (Perú).
 * Devuelve también la foto ORIGINAL (`monedaOrigen`/`montoOrigen`/`tcAplicado`, columnas de
 * trazabilidad agregadas por la migración `20261003e_comision_snapshot_moneda_origen.sql`) — nunca
 * se pierde el dato crudo, solo se corrige la base que usa el motor.
 * Ver `docs/decisiones/2026-10-03-comision-usd-monto-congelado-diagnostico.md`.
 *
 * @param {Object} oppLike  objeto con `moneda_dolar`/`importe` + fechas (`facturafecha`/`fecha_cierre`/
 *   `mes_cierre`, misma cascada que `getRateForOpp`)
 * @param {string} pais     'Chile' | 'Perú'
 * @returns {Promise<{monto:number, monedaOrigen:string, montoOrigen:number, tcAplicado:number}>}
 * @throws si `moneda_dolar=true` y no hay NINGÚN TC disponible — nunca congela en silencio (10.177)
 */
export const resolverMontoCongelado = async (oppLike, pais) => {
  const montoOrigen = Number(oppLike?.importe) || 0;
  if (!oppLike?.moneda_dolar) {
    return { monto: montoOrigen, monedaOrigen: monedaLocalDePais(pais), montoOrigen, tcAplicado: 1 };
  }
  await loadExchangeRates();
  const tcAplicado = getRateForOpp(oppLike, (fecha) => getDolarValue(fecha, pais), 0);
  if (!(tcAplicado > 0)) {
    throw new Error(
      `[comisionSnapshot] Sin TC disponible para convertir a moneda local (pais=${pais}) — no se puede ` +
        `congelar el monto en USD en silencio (10.177/10.185). Venta/mes: ${JSON.stringify({
          sale_id: oppLike.id,
          facturafecha: oppLike.facturafecha,
          fecha_cierre: oppLike.fecha_cierre,
          mes_cierre: oppLike.mes_cierre,
        })}`
    );
  }
  return { monto: montoOrigen * tcAplicado, monedaOrigen: "USD", montoOrigen, tcAplicado };
};

/**
 * Traza ESTRUCTURADA de eventos de snapshot (regla 10.177 — prohibido el fallo silencioso).
 * Marcador grep-able `[SNAPSHOT_TRACE]` para que una red de seguridad lo detecte. Cubre:
 *  - `recaptura_edit_fallo`: una re-captura en edición se tragaba en try/catch → ahora deja huella.
 *  - `facturacion_refresh_corrigio_stale`: el refresh al facturar corrigió una foto que estaba stale.
 *  - `congelarPct_error`: fallo del sellado al facturar.
 * Traza de consola (no persiste — alcanza en esta fase, sin panel). Nunca lanza.
 */
export const traceSnapshot = (evento, payload = {}) => {
  try {
    // eslint-disable-next-line no-console
    console.error("[SNAPSHOT_TRACE]", JSON.stringify({ evento, ...payload }));
  } catch {
    /* noop: la traza jamás debe romper el flujo que la emite */
  }
};

/**
 * Paginador interno: recorre TODAS las filas de una query en bloques de `page` con `.range()`.
 * Necesario porque el techo del server es 2.000 filas y `.limit()` NO lo sube (regla 10.167).
 * `buildQuery` devuelve un builder NUEVO por página (no reusar uno awaited). Ordena por `id`
 * para que la paginación sea estable (sin duplicar ni saltear filas entre bloques).
 * Si la query falla → throw (regla 10.177, nada de tragar a []).
 */
const fetchPaginado = async (buildQuery, page = 1000) => {
  const out = [];
  for (let from = 0; ; from += page) {
    const { data, error } = await buildQuery()
      .order("id", { ascending: true })
      .range(from, from + page - 1);
    if (error) throw error;
    if (data?.length) out.push(...data);
    if (!data || data.length < page) break;
  }
  return out;
};

/**
 * Carga la FOTO COMPLETA de comisión por venta de un país en UN solo objeto: el denominador del
 * padre (`comision_snapshot`) + el desglose por edificio del hijo (`comision_snapshot_edificio`),
 * cosidos por `snapshot_id`. Devuelve
 *   Map<key, { denominador_total, porEdificio: Map<edificioId, {asc,circ,esp,cow}> }>
 * con `key` = `keyDeFactura` ('s:{sale_id}' / 'c:{contrato_id}|{mes}').
 *
 * Fuente ÚNICA del snapshot para los motores (Fase 1): un solo mapa transporta AMBOS ejes
 * (numerador por edificio Y denominador). Lo consume `resolverDesgloseComision` (comisionEdificiosUtil).
 *
 * Reglas duras: si cualquier query falla → throw (10.177). El hijo tiene ~25.000 filas → se pagina
 * con `.range()` en `fetchPaginado` (10.167). El hijo no tiene columna `pais`: se scopea por país
 * filtrando por los `snapshot_id` de los padres de ese país.
 */
export const cargarSnapshotComision = async (pais) => {
  // 1) Padres del país. Se paginan también (fetchPaginado) por robustez ante crecimiento futuro.
  const padres = await fetchPaginado(() =>
    client
      .from("comision_snapshot")
      .select("id, origen_tipo, sale_id, contrato_id, mes, denominador_total")
      .eq("pais", pais)
  );

  const map = new Map();
  const keyPorSnapshotId = new Map();
  for (const p of padres) {
    const key = p.origen_tipo === "sale" ? `s:${p.sale_id}` : `c:${p.contrato_id}|${p.mes}`;
    map.set(key, { denominador_total: p.denominador_total, porEdificio: new Map() });
    keyPorSnapshotId.set(p.id, key);
  }

  // 2) Hijos de esos snapshots (paginados). Sin padres → no hay hijos que traer.
  const snapshotIds = [...keyPorSnapshotId.keys()];
  if (snapshotIds.length > 0) {
    const hijos = await fetchPaginado(() =>
      client
        .from("comision_snapshot_edificio")
        .select("id, snapshot_id, edificio_id, pantallas_asc, pantallas_circ, pantallas_esp, pantallas_cow")
        .in("snapshot_id", snapshotIds)
    );
    for (const h of hijos) {
      const key = keyPorSnapshotId.get(h.snapshot_id);
      if (!key) continue; // hijo cuyo padre no es de este país (no debería pasar por el filtro .in)
      map.get(key).porEdificio.set(Number(h.edificio_id), {
        asc: h.pantallas_asc || 0,
        circ: h.pantallas_circ || 0,
        esp: h.pantallas_esp || 0,
        cow: h.pantallas_cow || 0,
      });
    }
  }
  return map;
};

/**
 * Carga las FOTOS CONGELADAS del % de intermediación (A3, ver 7.22) de un país. SOLO filas con
 * `pct_congelado_en` NOT NULL. Map keyed IGUAL que `cargarSnapshotComision`/`keyDeFactura`
 * ('s:{sale_id}' / 'c:{contrato_id}|{mes}'); value = `{ agencia, apx, broker, descuento }` (%).
 * `map.has(key)` (o `map.get(key)` truthy) = "esta venta/mes tiene el % congelado → usarlo tal cual".
 * Si no está en el map → resolver en vivo. Una sola query, país-scopeada (misma vida que el snapMap).
 */
export const cargarSnapshotPctCongelado = async (pais) => {
  const { data, error } = await client
    .from("comision_snapshot")
    .select(
      "origen_tipo, sale_id, contrato_id, mes, pct_agencia_congelado, pct_apx_congelado, pct_broker_congelado, pct_descuento_congelado, monto_congelado, ejecutivo_lider_congelado"
    )
    .eq("pais", pais)
    .not("pct_congelado_en", "is", null);
  if (error) throw error;
  const map = new Map();
  for (const r of data || []) {
    const key = r.origen_tipo === "sale" ? `s:${r.sale_id}` : `c:${r.contrato_id}|${r.mes}`;
    map.set(key, {
      agencia: Number(r.pct_agencia_congelado) || 0,
      apx: Number(r.pct_apx_congelado) || 0,
      broker: Number(r.pct_broker_congelado) || 0,
      descuento: Number(r.pct_descuento_congelado) || 0,
      // Monto congelado al facturar (=importe de la venta/mes en ese instante). Lo lee el motor comercial
      // para usar la base CONGELADA en vez del importe vivo cuando la fila está sellada. null → sin monto
      // congelado (foto vieja sin capturar el monto) → el motor cae al importe vivo.
      monto: r.monto_congelado != null ? Number(r.monto_congelado) : null,
      // Líder de la cuenta (Chile, 4%/1% de Nicolás) congelado al sellar — NULL para Perú (no se congela
      // todavía, 10.287/regla nueva) o para fotos selladas ANTES de este fix → el motor cae a
      // `clientes[client_id].ejecutivo` en vivo, comportamiento idéntico a hoy.
      ejecutivoLiderCongelado: r.ejecutivo_lider_congelado != null ? Number(r.ejecutivo_lider_congelado) : null,
    });
  }
  return map;
};

/**
 * Captura (o re-captura) la foto de comisión de una venta o mes-de-contrato.
 *
 * Congela el desglose de pantallas por edificio (4 circuitos) en las tablas
 * comision_snapshot (padre) + comision_snapshot_edificio (hijos), de modo que
 * editar el inventario de un edificio NO mueva la comisión histórica (deuda 9.19).
 *
 * Idempotente: si ya existe foto vigente para ese sale_id o (contrato_id, mes),
 * la reemplaza (borra la anterior — CASCADE borra hijos — y reescribe). Soporta
 * 'creacion', 'modificacion' y futura re-captura sin duplicar (respeta el UNIQUE parcial).
 *
 * Corre con el rol authenticated (la policy "Authenticated all" lo permite).
 * NO lee ni toca sales/sales_contratos/comisiones — solo escribe las tablas snapshot.
 *
 * @param {Object} p
 * @param {'sale'|'contrato_mes'} p.origen_tipo
 * @param {number} [p.sale_id]       requerido si origen_tipo='sale'
 * @param {number} [p.contrato_id]   requerido si origen_tipo='contrato_mes'
 * @param {string} [p.mes]           "MM-YYYY", requerido si origen_tipo='contrato_mes'
 * @param {Array}  p.edificios       objetos edificio participantes (con pantallas/totems/espera/cowork)
 * @param {Object} p.csvs            { pantallas_ascensores, pantallas_circulacion, pantallas_espera, pantallas_cowork }
 * @param {number} [p.monto]         importe congelado de la venta/mes, YA EN MONEDA LOCAL
 *   (resolver con `resolverMontoCongelado` antes de llamar — nunca pasar el crudo en USD)
 * @param {string} [p.monedaOrigen]  'CLP'/'PEN'/'USD' — moneda ORIGINAL de `montoOrigen` (trazabilidad)
 * @param {number} [p.montoOrigen]   importe ORIGINAL sin convertir (= `monto` si no era USD)
 * @param {number} [p.tcAplicado]    TC aplicado para llegar a `monto` (1 si no era USD)
 * @param {string} p.pais
 * @param {'creacion'|'modificacion'|'backfill'} p.motivo
 */
export const capturarSnapshotComision = async ({
  origen_tipo,
  sale_id = null,
  contrato_id = null,
  mes = null,
  edificios = [],
  csvs = {},
  monto = null,
  monedaOrigen = null,
  montoOrigen = null,
  tcAplicado = null,
  pais,
  motivo,
}) => {
  try {
    // 1. Desglose por circuito de cada edificio participante (hijos)
    const hijos = (edificios || []).map((e) => ({
      edificio_id: e.id,
      ...getDesglosePorEdificio(e, csvs),
    }));

    // 2. Invariante: el denominador del padre es la suma de los 4 circuitos de los hijos
    const denominador_total = hijos.reduce(
      (acc, h) => acc + h.pantallas_asc + h.pantallas_circ + h.pantallas_esp + h.pantallas_cow,
      0
    );

    // 2b. GUARD ANTI-DEGENERADO (regla 10.177 — prohibido el fallo silencioso): si el documento SÍ
    //     tiene edificios participantes pero la foto quedaría INSERVIBLE (denominador <= 0), NO se
    //     persiste una foto vacía — en el motor daría proporcion = numerador/0 = 0 → $0 en silencio
    //     para TODOS los edificios (el bug de fondo). Se corta ANTES de borrar/insertar (no se toca la
    //     foto existente) y se deja traza con la key del documento. Un doc SIN edificios (edificios=[])
    //     NO llega acá: los callers ya no lo capturan (contrato → `vacio`; venta → sin participantes).
    if ((edificios || []).length > 0 && !(denominador_total > 0)) {
      traceSnapshot("captura_degenerada_bloqueada", {
        origen_tipo,
        sale_id,
        contrato_id,
        mes,
        pais,
        edificios: edificios.length,
        denominador_total,
      });
      return { snapshot_id: null, denominador_total, hijos: hijos.length, skipped: "degenerada_con_edificios" };
    }

    // 3a. SEAL GUARD (blindaje de facturación): una foto SELLADA (`pct_congelado_en` NOT NULL) es
    //     DEFINITIVA — ni edits posteriores, ni re-conciliación, ni cambios de ficha de edificio la
    //     mueven. `capturarSnapshotComision` se vuelve NO-OP sobre ella. El refresh de facturación
    //     pasa porque ocurre ANTES de sellar (pct_congelado_en aún NULL); desfacturar
    //     (`invalidarPctCongelado`) la des-sella y vuelve a habilitar la re-captura. Reemplaza al
    //     carry-forward viejo (que preservaba el pct al SOBRESCRIBIR — ahora directamente no se toca).
    let selPrev = client.from("comision_snapshot").select("id, pct_congelado_en").eq("origen_tipo", origen_tipo);
    selPrev =
      origen_tipo === "sale" ? selPrev.eq("sale_id", sale_id) : selPrev.eq("contrato_id", contrato_id).eq("mes", mes);
    const { data: prevRows } = await selPrev;
    const prevPadre = prevRows?.[0];
    if (prevPadre?.pct_congelado_en != null) {
      return { snapshot_id: prevPadre.id, denominador_total: null, hijos: 0, skipped: "sellada" };
    }

    // 3b. Idempotencia: borrar la foto vigente de esta clave (CASCADE borra los hijos)
    let delQ = client.from("comision_snapshot").delete().eq("origen_tipo", origen_tipo);
    delQ = origen_tipo === "sale" ? delQ.eq("sale_id", sale_id) : delQ.eq("contrato_id", contrato_id).eq("mes", mes);
    const { error: delErr } = await delQ;
    if (delErr) throw delErr;

    // 4. Insertar el padre y obtener su id (foto SIN pct: nace sin sellar → el sellado lo pone
    //    `congelarPctIntermediacion` al facturar; una foto sellada nunca llega hasta acá, ver 3a).
    const { data: padre, error: padreErr } = await client
      .from("comision_snapshot")
      .insert({
        origen_tipo,
        sale_id,
        contrato_id,
        mes,
        denominador_total,
        monto_congelado: monto,
        moneda_origen: monedaOrigen,
        monto_origen: montoOrigen,
        tc_aplicado: tcAplicado,
        pais,
        motivo_captura: motivo,
      })
      .select("id")
      .single();
    if (padreErr) throw padreErr;

    // 5. Insertar los hijos
    if (hijos.length > 0) {
      const { error: hijosErr } = await client
        .from("comision_snapshot_edificio")
        .insert(hijos.map((h) => ({ snapshot_id: padre.id, ...h })));
      if (hijosErr) throw hijosErr;
    }

    return { snapshot_id: padre.id, denominador_total, hijos: hijos.length };
  } catch (error) {
    log.error("capturarSnapshotComision", error);
    throw error;
  }
};

// Trae los edificios vivos (sus 4 circuitos) por lista de IDs.
const fetchEdificiosPorIds = async (ids) => {
  if (ids.length === 0) return [];
  const { data, error } = await client.from("edificios").select("id, pantallas, totems, espera, cowork").in("id", ids);
  if (error) throw error;
  return data || [];
};

const parseIds = (csv) =>
  (csv || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

/**
 * Orquesta la captura del snapshot a partir de una fila de `sales` recién creada/editada.
 * Toda venta con edificios participantes nace con foto. Sin CSV de circuitos (carga manual
 * Perú) → `getDesglosePorEdificio` usa la rama `camposVacios` (4 circuitos del edificio),
 * mismo criterio que el backfill (CABO 1). Solo se saltea si no hay edificios participantes.
 */
export const capturarSnapshotDesdeVenta = async (venta, motivo = "creacion") => {
  const csvs = {
    pantallas_ascensores: venta.pantallas_ascensores,
    pantallas_circulacion: venta.pantallas_circulacion,
    pantallas_espera: venta.pantallas_espera,
    pantallas_cowork: venta.pantallas_cowork,
  };

  const ids = parseIds(venta.edificiosparticipantes);
  if (ids.length === 0) return null;
  const edificios = await fetchEdificiosPorIds(ids);
  const { monto, monedaOrigen, montoOrigen, tcAplicado } = await resolverMontoCongelado(venta, venta.pais);

  return capturarSnapshotComision({
    origen_tipo: "sale",
    sale_id: venta.id,
    edificios,
    csvs,
    monto,
    monedaOrigen,
    montoOrigen,
    tcAplicado,
    pais: venta.pais,
    motivo,
  });
};

/**
 * Orquesta la captura de un contrato: N fotos, UNA por mes, cada una con los edificios
 * y los 4 CSV de ESE mes (no los del contrato entero). Keyed por (contrato_id, mes).
 */
export const capturarSnapshotDesdeContrato = async (contrato, motivo = "creacion") => {
  const meses = contrato.meses || [];
  // Reunir TODOS los ids que podrían participar: `edificios_ids` MÁS los 4 CSV de `pantallas_*` de
  // cada mes. Antes solo se miraba `edificios_ids` → cuando venía NULL no se traía ningún edificio y
  // la foto salía vacía, mientras el motor vivo sí leía `pantallas_*` (bug real: sales_contratos 11).
  const allIds = [
    ...new Set(
      meses.flatMap((m) => [
        ...parseIds(m.edificios_ids),
        ...parseIds(m.pantallas_ascensores),
        ...parseIds(m.pantallas_circulacion),
        ...parseIds(m.pantallas_espera),
        ...parseIds(m.pantallas_cowork),
      ])
    ),
  ];
  if (allIds.length === 0) return [];

  const edifList = await fetchEdificiosPorIds(allIds);
  const edifMap = Object.fromEntries(edifList.map((e) => [String(e.id), e]));

  const results = [];
  for (const m of meses) {
    // FUENTE ÚNICA (regla 10.1): resolver circuitos + participantes con la MISMA lógica que el motor
    // vivo (explodarContratosComoFacturas) → pantallas_* con fallback a derivar de edificios_ids. Con
    // esto el denominador congelado == el denominador vivo, aun cuando `edificios_ids` es NULL.
    const { pAsc, pEsp, pCir, pCow, edificiosParticipantes, vacio } = resolverCircuitosParticipantesMes(m, edifMap);
    if (vacio) continue; // el mes realmente no tiene edificios → no se captura foto (correcto)
    const edificios = parseIds(edificiosParticipantes)
      .map((id) => edifMap[id])
      .filter(Boolean);
    const csvs = {
      pantallas_ascensores: pAsc,
      pantallas_circulacion: pCir,
      pantallas_espera: pEsp,
      pantallas_cowork: pCow,
    };
    // eslint-disable-next-line no-await-in-loop
    const { monto, monedaOrigen, montoOrigen, tcAplicado } = await resolverMontoCongelado(
      { moneda_dolar: contrato.moneda_dolar, importe: m.importe, facturafecha: m.facturafecha, mes_cierre: m.mes },
      contrato.pais
    );
    // eslint-disable-next-line no-await-in-loop
    const r = await capturarSnapshotComision({
      origen_tipo: "contrato_mes",
      contrato_id: contrato.id,
      mes: m.mes,
      edificios,
      csvs,
      monto,
      monedaOrigen,
      montoOrigen,
      tcAplicado,
      pais: contrato.pais,
      motivo,
    });
    results.push(r);
  }
  return results;
};

/**
 * Borra las fotos de meses que ya NO existen en el contrato (CABO 2: evita huérfanas).
 * Al eliminar un mes de un `sales_contratos`, su foto `contrato_mes` quedaría colgada
 * (la re-captura solo toca meses presentes, no borra los ausentes). CASCADE borra los hijos.
 * Aditivo: el caller la envuelve en try/catch para no abortar el update.
 */
export const eliminarSnapshotsContratoMeses = async (contrato_id, meses) => {
  if (!contrato_id || !meses || meses.length === 0) return;
  const { error } = await client
    .from("comision_snapshot")
    .delete()
    .eq("origen_tipo", "contrato_mes")
    .eq("contrato_id", contrato_id)
    .in("mes", meses);
  if (error) throw error;
};

/**
 * Invalida (limpia) la foto del % congelado de un padre: setea las 6 columnas pct_* a NULL (rama
 * all-null de chk_pct_congelado_coherencia). Se llama al DESfacturar (A4, ver 7.22): así una
 * re-facturación posterior vuelve a congelar al % vigente de ese momento (congelarPctIntermediacion
 * es idempotente y verá pct_congelado_en NULL → re-congela). Aditivo, no lanza. Por venta (sale_id)
 * o (contrato_id, mes) — no toca meses hermanos. Si ya estaba en NULL → UPDATE inocuo.
 */
export const invalidarPctCongelado = async ({ origenTipo, saleId = null, contratoId = null, mes = null }) => {
  try {
    let q = client.from("comision_snapshot").update({
      pct_agencia_congelado: null,
      pct_apx_congelado: null,
      pct_broker_congelado: null,
      pct_descuento_congelado: null,
      pct_congelado_en: null,
      pct_congelado_motivo: null,
      ejecutivo_lider_congelado: null,
    });
    q =
      origenTipo === "sale"
        ? q.eq("origen_tipo", "sale").eq("sale_id", saleId)
        : q.eq("origen_tipo", "contrato_mes").eq("contrato_id", contratoId).eq("mes", mes);
    const { error } = await q;
    if (error) throw error;
  } catch (e) {
    log.error("invalidarPctCongelado", e);
  }
};

// ── Flag de sensibilidad: qué cambios de una venta/mes obligan a re-capturar la foto ──
// SOLO estos campos mueven la proporción o el monto del snapshot.
export const CAMPOS_SENSIBLES_VENTA = [
  "edificiosparticipantes",
  "pantallas_ascensores",
  "pantallas_circulacion",
  "pantallas_espera",
  "pantallas_cowork",
  "pantallastotales",
  "importe",
];

// Equivalente a nivel de cada mes de un contrato.
export const CAMPOS_SENSIBLES_MES = [
  "edificios_ids",
  "pantallas_ascensores",
  "pantallas_circulacion",
  "pantallas_espera",
  "pantallas_cowork",
  "pantallas_totales",
  "importe",
];

/**
 * true SOLO si cambió algún campo sensible (proporción/monto) entre antes y después.
 * Inocuos (NO disparan): facturanumero/facturafecha/facturafechacobro/plazocobro/
 * fecha_cobro_estimada/otros_costos/eliminada/intermediario_override_id/estatus/etc.
 * Comparación estricta: nunca omite un cambio real (puede re-capturar de más ante
 * diferencias de tipo, que es el lado seguro — la captura es idempotente).
 */
export const cambioRequiereRecaptura = (antes, despues, campos = CAMPOS_SENSIBLES_VENTA) =>
  !!antes && !!despues && campos.some((c) => (antes[c] ?? null) !== (despues[c] ?? null));
